<template>
  <a-select
    class="comparison-select"
    :options="typeOptions"
    v-model.sync="localValue"
    :is-searchable="false"
    btn-color="clear-area"
    :has-teleport="!outsideModal">
  </a-select>
</template>

<script lang="ts">
  import Vue from "vue";
  import {IDefaultVOption} from "~/interfaces/vSelectInterfaces";
  import LocalValueEmissionMixin from "~/mixins/localValueEmissionMixin";
  import {ComparisonType} from "~/models/conditionalRules/comparisonType";
  import {TextComparison} from "~/models/conditionalRules/textComparison";
  import {SingleChoiceComparison} from "~/models/conditionalRules/singleChoiceComparison";
  import {MultiChoiceComparison} from "~/models/conditionalRules/multiChoiceComparison";
  import {NumericComparison} from "~/models/conditionalRules/numericComparison";
  import {InstanceCountComparison} from "~/models/conditionalRules/instanceCountComparison";
  import {DatetimeComparison} from "~/models/conditionalRules/datetimeComparison";
  import {AccountRelationComparison} from "~/models/conditionalRules/accountRelationComparison";
  import {RecordComparison} from '~/models/conditionalRules/recordComparison'
  import {BooleanComparison} from "~/models/conditionalRules/booleanComparison";
  import {AccountRelationUserComparison} from "~/models/conditionalRules/accountRelationUserComparison";
  import {BooleanHelper} from "~/helpers/booleanHelper";

  export default Vue.extend({
    props: ['value', 'type', 'showIsUpdatedComparison', 'showPreviousValueComparisonOptions', 'outsideModal', 'hideDatetimeIsWithin', 'showPresenceBasedComparisons'],
    mixins: [LocalValueEmissionMixin],
    data() {
      return {
        textOptions: [
          {label: 'is', value: TextComparison.is},
          {label: 'is not', value: TextComparison.isNot},
          {label: 'contains', value: TextComparison.contains},
          {label: 'does not contain', value: TextComparison.doesNotContain},
          {label: 'starts with', value: TextComparison.startsWith},
          {label: 'does not start with', value: TextComparison.doesNotStartWith},
          {label: 'ends with', value: TextComparison.endsWith},
          {label: 'does not end with', value: TextComparison.doesNotEndWith},
        ],
        singleChoiceOptions: [
          {label: 'is', value: SingleChoiceComparison.is},
          {label: 'is not', value: SingleChoiceComparison.isNot},
        ],
        multiChoiceOptions: [
          {label: 'includes', value: MultiChoiceComparison.includes},
          {label: 'does not include', value: MultiChoiceComparison.doesNotInclude},
        ],
        numericOptions: [
          {label: 'is equal to', value: NumericComparison.isEqualTo},
          {label: 'is greater than', value: NumericComparison.isGreaterThan},
          {label: 'is less than', value: NumericComparison.isLessThan},
          {label: 'is greater or equal to', value: NumericComparison.isGreaterOrEqualTo},
          {label: 'is less or equal to', value: NumericComparison.isLessOrEqualTo},
          {label: 'is not equal to', value: NumericComparison.isNotEqualTo},

        ],
        instanceCountOptions: [
          {label: 'has count equal to', value: InstanceCountComparison.hasCountEqualTo},
          {label: 'has count not equal to', value: InstanceCountComparison.hasCountNotEqualTo},
          {label: 'has count greater than', value: InstanceCountComparison.hasCountGreaterThan},
          {label: 'has count less than', value: InstanceCountComparison.hasCountLessThan},
          {label: 'has count greater or equal to', value: InstanceCountComparison.hasCountGreaterOrEqualTo},
          {label: 'has count less or equal to', value: InstanceCountComparison.hasCountLessOrEqualTo},

        ],
        datetimeOptions: [
          {label: 'is', value: DatetimeComparison.is},
          {label: 'is before', value: DatetimeComparison.isBefore},
          {label: 'is after', value: DatetimeComparison.isAfter},
          {label: 'is within', value: DatetimeComparison.isWithin},
        ],
        accountRelationOptions: [
          {label: 'is', value: AccountRelationComparison.is},
        ],
        booleanOptions: [
          {label: 'is', value: BooleanComparison.is},
          {label: 'is not', value: BooleanComparison.isNot},
        ],
        accountRelationUserOptions: [
          {label: 'is', value: AccountRelationUserComparison.is},
          {label: 'is not', value: AccountRelationUserComparison.isNot},
        ],
      }
    },
    mounted(): void {
      if (this.value === null) {
        this.resetDefaultOption();
      }
    },
    computed: {
      isUpdatedComparison(): IDefaultVOption {
        switch (this.type) {
          case ComparisonType.text:
            return {label: 'is updated', value: TextComparison.isUpdated};
          case ComparisonType.singleChoice:
            return {label: 'is updated', value: SingleChoiceComparison.isUpdated};
          case ComparisonType.multiChoice:
            return {label: 'is updated', value: MultiChoiceComparison.isUpdated};
          case ComparisonType.numeric:
            return {label: 'is updated', value: NumericComparison.isUpdated};
          case ComparisonType.instanceCount:
            return {label: 'is updated', value: InstanceCountComparison.isUpdated};
          case ComparisonType.datetime:
            return {label: 'is updated', value: DatetimeComparison.isUpdated};
          case ComparisonType.relativeUser:
            return {label: 'is updated', value: SingleChoiceComparison.isUpdated};
        }
      },
      presenceBasedComparisons(): IDefaultVOption[] {
        switch (this.type) {
          case ComparisonType.text:
            return [
              {label: 'is blank', value: TextComparison.isBlank},
              {label: 'is present', value: TextComparison.isPresent},
            ];
          case ComparisonType.singleChoice:
            return [
              {label: 'is blank', value: SingleChoiceComparison.isBlank},
              {label: 'is present', value: SingleChoiceComparison.isPresent},
            ];
          case ComparisonType.multiChoice:
            return [
              {label: 'is blank', value: MultiChoiceComparison.isBlank},
              {label: 'is present', value: MultiChoiceComparison.isPresent},
            ];
          case ComparisonType.numeric:
            return [
              {label: 'is blank', value: NumericComparison.isBlank},
              {label: 'is present', value: NumericComparison.isPresent},
            ];
          case ComparisonType.instanceCount:
            return [
              {label: 'is blank', value: InstanceCountComparison.isBlank},
              {label: 'is present', value: InstanceCountComparison.isPresent},
            ];
          case ComparisonType.datetime:
            return [
              {label: 'is blank', value: DatetimeComparison.isBlank},
              {label: 'is present', value: DatetimeComparison.isPresent},
            ];
          case ComparisonType.record:
            return [
              {label: 'is blank', value: RecordComparison.isBlank},
              {label: 'is present', value: RecordComparison.isPresent},
            ];
          default:
            return [];
        }
      },
      previousValueComparisonOptions():IDefaultVOption[]{
        switch (this.type) {
          case ComparisonType.text:
            return [{label: 'was', value: TextComparison.was},{label: 'was not', value: TextComparison.wasNot} ];
          case ComparisonType.singleChoice:
            return [{label: 'was', value: SingleChoiceComparison.was},{label: 'was not', value: SingleChoiceComparison.wasNot} ];
          case ComparisonType.numeric:
            return [{label: 'was', value: NumericComparison.was},{label: 'was not', value: NumericComparison.wasNot} ];
          case ComparisonType.instanceCount:
            return [{label: 'had count equal to', value: InstanceCountComparison.hadCountEqualTo},{label: 'had count not equal to', value: InstanceCountComparison.hadCountNotEqualTo} ];
          case ComparisonType.datetime:
            return [{label: 'was', value: DatetimeComparison.was},{label: 'was not', value: DatetimeComparison.wasNot} ];
          default:
            return [];
        }
      },
      typeOptions(): IDefaultVOption[] {
        let baseOptions;
        switch (this.type) {
          case ComparisonType.text:
            baseOptions = this.textOptions;
            break;
          case ComparisonType.singleChoice:
            baseOptions = this.singleChoiceOptions;
            break;
          case ComparisonType.multiChoice:
            baseOptions = this.multiChoiceOptions;
            break;
          case ComparisonType.numeric:
            baseOptions = this.numericOptions;
            break;
          case ComparisonType.instanceCount:
            baseOptions = this.instanceCountOptions;
            break;
          case ComparisonType.datetime:
            baseOptions = this.datetimeOptions;
            if (this.hideDatetimeIsWithin) {
              baseOptions = baseOptions.filter((option) => {
                return option.value !== DatetimeComparison.isWithin
              })
            }
            break;
          case ComparisonType.relativeUser:
            baseOptions = this.singleChoiceOptions;
            break;
          case ComparisonType.accountRelation:
            baseOptions = this.accountRelationOptions;
            break;
          case ComparisonType.boolean:
            baseOptions = this.booleanOptions;
            break;
          case ComparisonType.accountRelationUser:
            baseOptions = this.accountRelationUserOptions;
            break;
          case ComparisonType.record:
            baseOptions = this.presenceBasedComparisons;
            break;
          default:
            baseOptions = [];
        }
        if (this.showIsUpdatedComparison && this.type !== ComparisonType.relativeUser && this.type !== ComparisonType.accountRelationUser) {
          baseOptions.push(this.isUpdatedComparison)
        }
        if (this.showPresenceBasedComparisonsForType) {
          baseOptions.push(...this.presenceBasedComparisons);
        }
        return this.showPreviousValueComparisonOptions ?  [...baseOptions, ...this.previousValueComparisonOptions] : baseOptions
      },
      showPresenceBasedComparisonsForType(): boolean {
        if (this.type === ComparisonType.record) return false; // ComparisonType.record already has presence based comparisons in baseOptions

        if (this.showPresenceBasedComparisons instanceof Function) {
          return this.showPresenceBasedComparisons(this.type);
        }

        return BooleanHelper.isStrictTrue(this.showPresenceBasedComparisons);
      },
    },
    methods: {
      resetDefaultOption(): void {
        this.$emit('input', this.typeOptions[0].value);
      }
    },
    watch: {
      type(newType): void {
        this.resetDefaultOption();
      }
    }
  });
</script>